import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import React, { FunctionComponent, useEffect } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { TimeRelativeTo } from "@iventis/domain-model/model/timeRelativeTo";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import EnumToRadioButtons from "@iventis/components/src/enum-to-radio-buttons";
import { TimeRuleFormValues, TimeRulesEditor } from "./time-rules-editor";
import { TimeType } from "./time-rules.types";
import { FixedTimeEditor } from "./fixed-time-editor";
import { EventBlockTimeProperties, SetFieldValue } from "./schedule.types";
import { isRelativeRulesValid } from "./sub-event-relative.helpers";
import { TimeEstimateCheckbox } from "./estimate-checkbox";

export type TimeTypeDelegatorRef = { getTimeType(): TimeType };
export type TimeDelegatorError = Record<keyof EventBlockTimeProperties, string>;

export type TimeTypeDelegatorProps = {
    values: EventBlockTimeProperties;
    setFieldValue: SetFieldValue<EventBlockTimeProperties>;
    potentialEventBlocksToRelateTo: { id: string; name: string; type: ScheduleConfigEventBlockType; startAt?: Date; endAt?: Date }[];
    eventBlockTypes: ScheduleConfigEventBlockType[];
    currentEventBlockType: ScheduleConfigEventBlockType;
    scheduleDaysMap: Map<number, Date>;
    currentDate: Date;
    disableInputs: Partial<Record<keyof TimeRuleFormValues | keyof { timeType?: TimeType }, (values: TimeRuleFormValues) => boolean>>;
    hiddenInputs?: Partial<Record<keyof TimeRuleFormValues | keyof { timeType?: TimeType }, (values: TimeRuleFormValues) => boolean>>;
    hiddenValuesBasedOnRelatedType?: Partial<Record<number, { relativeTo: TimeRelativeTo[] }>>;
    initialFormValues?: Partial<TimeRuleFormValues>;
    className?: string;
    errors?: TimeDelegatorError;
    handleError?: (error: keyof EventBlockTimeProperties, valid: boolean) => void;
    timezone: string;
};

export const TimeTypeDelegator: FunctionComponent<TimeTypeDelegatorProps> = ({
    values,
    setFieldValue,
    potentialEventBlocksToRelateTo,
    eventBlockTypes,
    currentEventBlockType,
    scheduleDaysMap,
    currentDate,
    className,
    disableInputs,
    hiddenInputs,
    hiddenValuesBasedOnRelatedType,
    initialFormValues,
    errors,
    handleError,
    timezone,
}) => {
    const translate = useIventisTranslate();
    const { relativeEventRules, timeType } = values;
    // Validation for the relative event rules
    useEffect(() => {
        // Output if the relative event rules are valid or not
        handleError?.("relativeEventRules", isRelativeRulesValid(relativeEventRules));
    }, [relativeEventRules]);

    useEffect(() => {
        if (timeType === TimeType.Relative) {
            // If relative is selected revalidate inputs
            handleError?.("relativeEventRules", isRelativeRulesValid(relativeEventRules));
        } else if (timeType === TimeType.Fixed) {
            // If time type is fixed remove all relative errors
            handleError?.("relativeEventRules", true);
        }
    }, [timeType]);

    return (
        <div className={className}>
            <div className="field-group">
                <StyledFieldLabel>{translate(Content.timerules.type.type_of_start_time)}</StyledFieldLabel>
                <EnumToRadioButtons<TimeType>
                    radioButtons={[
                        { type: TimeType.Relative, name: translate(Content.timerules.type.relative_time), description: translate(Content.timerules.type.relative_description) },
                        { type: TimeType.Fixed, name: translate(Content.timerules.type.fixed_time), description: translate(Content.timerules.type.fixed_description) },
                    ]}
                    value={timeType}
                    onChange={(value) => setFieldValue("timeType", TimeType[TimeType[value]])}
                />
            </div>
            {timeType === TimeType.Fixed && <FixedTimeEditor values={values} setFieldValue={setFieldValue} includeEndTime={!hiddenInputs.duration} errors={errors} />}
            {timeType === TimeType.Relative && (
                <>
                    <TimeRulesEditor
                        values={values}
                        setFieldValue={setFieldValue}
                        potentialEventBlocksToRelateTo={potentialEventBlocksToRelateTo}
                        eventBlockTypes={eventBlockTypes}
                        currentEventBlockType={currentEventBlockType}
                        scheduleDaysMap={scheduleDaysMap}
                        currentDate={currentDate}
                        disableInputs={disableInputs}
                        hiddenInputs={hiddenInputs}
                        hiddenValuesBasedOnRelatedType={hiddenValuesBasedOnRelatedType}
                        initialFormValues={initialFormValues}
                        timezone={timezone}
                    />
                </>
            )}
            <div className="checkbox-container">
                <StyledFieldLabel>{translate(Content.schedule.scheduled_item.estimated_duration)}</StyledFieldLabel>
                <TimeEstimateCheckbox checked={values.estimatedTime} onChange={(event) => setFieldValue("estimatedTime", event.target.checked)} />
            </div>
        </div>
    );
};
